import React from 'react';

import { AuthErrorDisplay } from '@/components/auth/AuthErrorDisplay';
import { TooManyAttemptsError } from '@/components/auth/TooManyAttemptsError';
import { VeraLoader } from '@/components/VeraLoader';
import { useAuth } from '@/data/hooks/auth';
import authService from '@/data/services/auth.service';
import { createScudRedirectUrl } from '@/utils/auth/url.utils';

interface AuthGuardProps {
  children: React.ReactNode;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const skipAuth = process.env.VITE_APP_SKIP_AUTH === 'true';
  const isDevelopment = process.env.VITE_APP_BUILD_ENV === 'dev';

  // * Emergency brake - prevent infinite loops
  const attempts = parseInt(
    localStorage.getItem('AUTH_REDIRECT_ATTEMPTS') || '0',
  );

  // * Check for authentication errors
  const authError = authService.getAuthError();

  console.log('🔍 AuthGuard render:', { attempts, authError });

  // * Skip auth mode if explicitly set
  if (skipAuth) {
    console.log('🔓 Auth skipped via environment variable');
    return <>{children}</>;
  }

  const token = authService.getToken();
  const isAuthenticated = user.isAuthenticated;

  // * Check for authentication API errors first
  if (authError) {
    console.log('💥 Recent authentication API error detected:', authError);
    return (
      <AuthErrorDisplay
        error={authError}
        isDevelopment={isDevelopment}
        onRetry={() => {
          authService.clearAllAuthData();
          window.location.reload();
        }}
      />
    );
  }

  // * Loading state - show spinner while auth is in progress
  if (isLoading) {
    console.log('⏳ Auth loading in progress');
    return <VeraLoader fullPage />;
  }

  console.log('⏳ AuthGuard loading check:', {
    isLoading,
    hasTokenInUrl: authService.hasTokenInUrl(),
  });

  // Emergency brake - too many attempts (check before redirecting)
  if (attempts >= 3) {
    console.log('🚨 Emergency brake activated - too many attempts');
    return (
      <TooManyAttemptsError
        attempts={attempts}
        maxAttempts={3}
        isDevelopment={isDevelopment}
        onRetry={() => {
          authService.clearAllAuthData();
          window.location.reload();
        }}
      />
    );
  }

  // * Remove duplicate token processing - this should be handled in __root.tsx only
  if (!token && !authError) {
    // Redirect to SCUD
    const newAttempts = attempts + 1;
    const loginUri = createScudRedirectUrl(
      window.location.href,
      String(process.env.VITE_APP_LOGIN_URL),
    );

    localStorage.setItem('AUTH_REDIRECT_ATTEMPTS', String(newAttempts));

    console.log(`🚀 Redirecting to SCUD (${newAttempts}/3):`, loginUri);
    window.location.href = loginUri;

    return <VeraLoader fullPage />;
  }

  console.log('🔍 Auth check:', {
    hasToken: !!token,
    isAuthenticated,
    attempts,
  });

  // * User is authenticated - show content
  if (token && isAuthenticated) {
    console.log('✅ User authenticated, showing content');
    return <>{children}</>;
  }

  // * No token and not authenticated - show loading
  return <VeraLoader fullPage />;
};
